import React from 'react';
import { Box, Flex, HStack, Icon, Link, Text } from '@chakra-ui/react';
import { FaStore } from 'react-icons/fa';
import Environment from 'environment';
import { ff } from '../../../theme';

interface BrandRedirectNoticeProps {
  variant?: 'inline' | 'alert' | 'pill';
  message?: string;
}

/**
 * Points brands who land on the creator app to the brand dashboard
 * (dashboard.foodfluence.io).
 *
 * - pill: "Are you a brand? | Brand Sign In →" bar for the login landing page
 * - inline: one-line footer for other auth pages
 * - alert: notice shown when a brand account is positively detected,
 *   with an optional message shown above the redirect line
 */
const BrandRedirectNotice: React.FC<BrandRedirectNoticeProps> = ({ variant = 'inline', message }) => {
  const dashboardUrl = Environment.BRAND_DASHBOARD_URL || 'https://dashboard.foodfluence.io';

  const redirectLine = (
    <>
      This login is for creators. Please access our brand sign in page{' '}
      <Link href={dashboardUrl} color={ff.ink} fontWeight={600} textDecoration="underline">
        here
      </Link>
      .
    </>
  );

  if (variant === 'pill') {
    return (
      <Flex align="center" width="100%" gap={3}>
        <Box flex="1" h="1px" bg={ff.line} />
        <HStack
          spacing={3}
          bg="white"
          border="1px solid"
          borderColor={ff.line}
          borderRadius="full"
          px={4}
          minH="44px"
          flexShrink={0}
        >
          <Icon as={FaStore} color={ff.ink} boxSize={4} />
          <Text fontSize="sm" fontWeight={500} color={ff.body} whiteSpace="nowrap">
            Are you a brand?
          </Text>
          <Box w="1px" h="18px" bg={ff.border} />
          <Link href={dashboardUrl} color={ff.ink} fontWeight={600} fontSize="sm" textDecoration="underline" whiteSpace="nowrap">
            Brand sign in &rarr;
          </Link>
        </HStack>
        <Box flex="1" h="1px" bg={ff.line} />
      </Flex>
    );
  }

  if (variant === 'alert') {
    return (
      <Box
        bg={ff.cream}
        border="1px solid"
        borderColor={ff.line}
        borderRadius="14px"
        p={4}
        width="100%"
      >
        {message && (
          <Text fontSize="sm" fontWeight={600} color={ff.ink}>
            {message}
          </Text>
        )}
        <Text fontSize="sm" color={ff.body} mt={message ? '2' : '0'}>
          {redirectLine}
        </Text>
      </Box>
    );
  }

  return (
    <Text fontSize="sm" color={ff.text2} textAlign="center">
      {redirectLine}
    </Text>
  );
};

export default BrandRedirectNotice;
